import { ArrowBack } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { type OrganizationRecord, useAllOrganizations } from "@/hooks/use-all-organizations";
import { useSaveDeliveryOption } from "@/hooks/use-delivery-option";
import { useDeliveryOptionForm } from "@/hooks/use-delivery-option-form";
import { useFindDeliveryOptionQuery } from "@/libs/generated/hooks";
import type { FindDeliveryOptionQuery } from "@/libs/generated/operations";
import { buildAssignments, errMsg, toFormValues } from "@/utils/delivery-options";
import { BasicInfoSection } from "../components/Delivery/delivery-options/components/BasicInfoSection";
import { BehaviorSection } from "../components/Delivery/delivery-options/components/BehaviorSection";
import { VisibilitySection } from "../components/Delivery/delivery-options/components/VisibilitySection";

type DeliveryOption = FindDeliveryOptionQuery["findDeliveryOption"];
interface FormProps {
  record: DeliveryOption;
  orgs: OrganizationRecord[];
  optionId: number;
}

function EditDeliveryOptionForm({ record, orgs, optionId }: FormProps) {
  const navigate = useNavigate();
  const { saveOption, isPending } = useSaveDeliveryOption();

  const [error, setError] = useState<string | null>(null);
  const form = useDeliveryOptionForm({
    defaultValues: {
      ...toFormValues(record),
      partnerAssignments: buildAssignments(orgs, record.partnerAssignments, record.name),
    },
    onSubmit: async (value) => {
      setError(null);
      if (record.visibilityMode === "PARTNER_LIST" && value.visibilityMode === "GLOBAL") {
        const hasCustomData = value.partnerAssignments.some(
          (a) => (a.clientPrice ?? 0) > 0 || a.displayName.trim() !== ""
        );
        if (
          hasCustomData &&
          !window.confirm("Switching to Global removes partner overrides. Continue?")
        )
          return;
      }
      try {
        await saveOption({ isEdit: true, optionId, value });
        navigate("/admin/options");
      } catch (err) {
        setError(errMsg(err));
      }
    },
  });

  return (
    <Box sx={{ px: 3, py: 3 }}>
      <Box sx={{ bgcolor: "background.default", px: 2, py: 2, mb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip title="Back">
              <IconButton
                size="small"
                onClick={() => navigate("/admin/options")}
                disabled={isPending}
              >
                <ArrowBack fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                Edit option
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {record.code} · #{optionId}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={record.active ? "Active" : "Inactive"}
              color={record.active ? "success" : "default"}
              size="small"
              variant="outlined"
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate("/admin/options")}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="small"
              loading={isPending}
              form="edit-option-form"
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </Box>

      <form.Subscribe
        selector={(state) => ({
          submitError: state.errorMap.onSubmit,
          attempts: state.submissionAttempts,
          isValid: state.isValid,
        })}
      >
        {({ submitError, attempts, isValid }) => {
          if (attempts === 0 || isValid || !submitError) return null;
          const message =
            typeof submitError === "string"
              ? submitError
              : "Le formulaire contient des erreurs. Vérifiez les champs en surbrillance.";
          return (
            <Alert severity="error" sx={{ mb: 2 }}>
              {message}
            </Alert>
          );
        }}
      </form.Subscribe>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form
        id="edit-option-form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.AppForm>
          <Grid container spacing={2} mt={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <BasicInfoSection form={form} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <BehaviorSection form={form} />
            </Grid>
            <Grid size={12}>
              <VisibilitySection form={form} />
            </Grid>
          </Grid>
        </form.AppForm>
      </form>
    </Box>
  );
}

function EditDeliveryOption() {
  const { id } = useParams();
  const optionId = Number(id);
  const isValidId = Boolean(id) && !Number.isNaN(optionId);

  const {
    data: recordData,
    isLoading: recordLoading,
    error: queryError,
  } = useFindDeliveryOptionQuery({ id: isValidId ? optionId : 0 }, { enabled: isValidId });

  const { organizations: orgs, isLoading: orgsLoading } = useAllOrganizations();

  if (!isValidId) {
    return <Box sx={{ p: 3 }}>Invalid delivery option id.</Box>;
  }

  if (recordLoading || orgsLoading) {
    return <Box sx={{ p: 3 }}>Loading...</Box>;
  }

  if (queryError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{errMsg(queryError)}</Alert>
      </Box>
    );
  }

  const record = recordData?.findDeliveryOption;

  if (!record) {
    return <Box sx={{ p: 3 }}>Delivery option not found.</Box>;
  }

  return <EditDeliveryOptionForm record={record} orgs={orgs} optionId={optionId} />;
}

export default EditDeliveryOption;
